import {useMemo} from 'react';

import type {FrontendTmdb} from '@repo/shared/api/website_api';
import {filterCatalogue} from '@repo/shared/lib/catalogue_filters';
import type {NonEmptyArray} from '@repo/shared/lib/type_utils';

import {
  useNumberQueryString,
  useStringArrayQueryString,
  useStringQueryString,
} from '@repo/shared-web/lib/use_query_string';

import {TmdbSort} from '@repo/website-frontend/components/search_bar';
import {useAllTmdb} from '@repo/website-frontend/stores/tmdb_store';

export enum GenreFilter {
  Include = 'include',
  Exclude = 'exclude',
}

export function serializeGenre(genre: {name: string; mode: GenreFilter}): string {
  return `${genre.name}:${genre.mode}`;
}

export function parseGenre(genre: string): {name: string; mode: GenreFilter} {
  const [name = '', mode = ''] = genre.split(':');
  return {name, mode: mode as GenreFilter};
}

const WEEKS = 7 * 24 * 3600 * 1000;
const MONTHS = 30.5 * 24 * 3600 * 1000;
const YEARS = 365 * 24 * 3600 * 1000;

export const MAX_AGE_STEPS: NonEmptyArray<{value: number; label: string; hasButton?: true}> = [
  {value: Number.POSITIVE_INFINITY, label: 'All', hasButton: true},
  {value: 10 * YEARS, label: '10y', hasButton: true},
  {value: 8 * YEARS, label: '8y'},
  {value: 5 * YEARS, label: '5y'},
  {value: 4 * YEARS, label: '4y'},
  {value: 3 * YEARS, label: '3y'},
  {value: 2 * YEARS, label: '2y', hasButton: true},
  {value: YEARS, label: '1y'},
  {value: 10 * MONTHS, label: '10m'},
  {value: 8 * MONTHS, label: '8m'},
  {value: 6 * MONTHS, label: '6m', hasButton: true},
  {value: 5 * MONTHS, label: '5m'},
  {value: 4 * MONTHS, label: '4m'},
  {value: 3 * MONTHS, label: '3m'},
  {value: MONTHS, label: '1m', hasButton: true},
  {value: 3 * WEEKS, label: '3w'},
  {value: 2 * WEEKS, label: '2w'},
  {value: WEEKS, label: '1w', hasButton: true},
];
export const MAX_AGE_STEPS_VISIBLE = MAX_AGE_STEPS.filter(step => step.hasButton);

export function useTmdbSearchBar() {
  const [urlGenres, setUrlGenres] = useStringArrayQueryString('genres', []);
  const genres = useMemo(() => urlGenres.map(parseGenre), [urlGenres]);

  const [minScore, setMinScore] = useNumberQueryString('minScore', 0);
  const [maxAge, setMaxAge] = useStringQueryString('maxAge', '');
  const [sort, setSort] = useStringQueryString<TmdbSort>('sort', TmdbSort.Recents);
  const [title, setTitle] = useStringQueryString('title', '');

  return {
    genres,
    minScore,
    maxAge,
    sort,
    title,
    setUrlGenres,
    setMinScore,
    setMaxAge,
    setSort,
    setTitle,
  };
}

export const useFilteredItems = (): FrontendTmdb[] => {
  const allTmdb = useAllTmdb();
  const {title, genres, minScore, maxAge, sort} = useTmdbSearchBar();

  const items = useMemo(() => [...allTmdb.values()], [allTmdb]);
  const filteredItems = useMemo(() => {
    return filterCatalogue(
      items,
      {
        title,
        minScore,
        maxAgeMs:
          MAX_AGE_STEPS.find(step => step.label === maxAge)?.value ?? Number.POSITIVE_INFINITY,
        includeGenres: genres
          .filter(genre => genre.mode === GenreFilter.Include)
          .map(genre => genre.name),
        excludeGenres: genres
          .filter(genre => genre.mode === GenreFilter.Exclude)
          .map(genre => genre.name),
        sort,
      },
      item => ({...item, genres: item.genres.map(genre => genre.name)}),
    );
  }, [items, sort, title, minScore, maxAge, genres]);

  return filteredItems;
};
